import { ReactNode } from "react";

import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import useTypedContext from "hooks/useTypedContext";
import UserStatusBadge from "components/UserStatusBadge";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import Tab from "ds/components/Tab";
import { AccountContext } from "views/AccountWrapper";
import Button from "ds/components/Button";
import useCanManageUsers from "shared/User/access/useCanManageUsers";
import useCanViewUsersPersonalApiKeys from "shared/ApiKey/access/useCanViewUsersPersonalApiKeys";

import { getCanRevokeUserAccess } from "../helpers";
import { showUserListRevokeAccessConfirmation } from "../Users/ListNew/Item/Dropdown/ConfirmationModal";
import { UserViewDetails } from "./types";
import { showUsersDetailsDrawer } from "../Users/DetailsDrawer";
import { SettingsContext } from "../Context";

type UserPageLayoutProps = {
  children: ReactNode;
  user: UserViewDetails;
};

const UserPageLayout = ({ children, user }: UserPageLayoutProps) => {
  const { viewer } = useTypedContext(AccountContext);
  const { activationStatus } = useTypedContext(SettingsContext);
  const canManageUsers = useCanManageUsers();
  const canViewUsersPersonalApiKeys = useCanViewUsersPersonalApiKeys();

  const userUrl = `/settings/user/${user.id}`;

  const canRevokeAccess = getCanRevokeUserAccess({ canManageUsers, viewer, user });

  return (
    <>
      <ViewHeader firstLevel>
        <Box fullWidth justify="between" align="center">
          <Box gap="medium" align="center">
            <ViewHeaderTitle tag="h2">{user.username}</ViewHeaderTitle>
            <UserStatusBadge status={user.status} />
          </Box>

          <Box gap="medium" align="center">
            <Button
              variant="secondary"
              onPress={() =>
                showUsersDetailsDrawer({
                  id: user.id,
                  userId: user.id,
                  activationStatus,
                })
              }
            >
              Details
            </Button>
            {canRevokeAccess && (
              <Button
                variant="dangerSecondary"
                onPress={() =>
                  showUserListRevokeAccessConfirmation({
                    id: user.id,
                    userName: user.username,
                  })
                }
              >
                Revoke access
              </Button>
            )}
          </Box>
        </Box>
        <ViewHeaderNavigation>
          <Tab end to={userUrl} label="Roles" />
          <Tab to={`${userUrl}/idp-groups`} label="IdP groups" />
          <Tab to={`${userUrl}/integrations`} label="Integrations" />
          {canViewUsersPersonalApiKeys && (
            <Tab
              to={`${userUrl}/personal-api-keys`}
              label="Personal API keys"
              suffixComponent={<Badge variant="blue" type="strong" text="BETA" />}
            />
          )}
        </ViewHeaderNavigation>
      </ViewHeader>

      {children}
    </>
  );
};

export default UserPageLayout;
